import { useNavigate, useSearch } from "@tanstack/react-router";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";

import { useRoster } from "../api";
import { CompanyDetailContent } from "./CompanyDetailContent";
import { CompanyQuickInfo } from "./CompanyQuickInfo";

/**
 * Drawer for one parent company. Mounted once inside ParentCompaniesPage;
 * visibility derives from the route's search params:
 *
 *   ?company=<id>             → quick view drawer
 *   ?company=<id>&expanded=1  → expanded drawer (tree + workflow)
 *   no `company`              → drawer closed
 *
 * The grid stays mounted behind both modes (scroll/filters survive).
 * Mirrors partners/PartnerDrawer.
 */
export function CompanyDrawer() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/parent-companies" });
  const roster = useRoster();
  const companyId = search.company ?? null;
  const expanded = !!search.expanded;
  const open = companyId != null;
  const tabSearch = search.tab ? { tab: search.tab } : {};

  const name =
    companyId != null
      ? roster.data?.parents.find((p) => p.id === companyId)?.name ?? companyId.slice(0, 8)
      : "";

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(v) => {
        if (!v && !expanded) {
          navigate({ to: "/parent-companies", search: tabSearch });
        }
      }}
      expanded={expanded}
      onExpandedChange={(exp) => {
        if (companyId == null) return;
        navigate({
          to: "/parent-companies",
          search: exp
            ? { ...tabSearch, company: companyId, expanded: true }
            : { ...tabSearch, company: companyId },
        });
      }}
      onClose={() => {
        if (companyId == null) return;
        // Peel: expanded → quick → closed.
        if (expanded) {
          navigate({ to: "/parent-companies", search: { ...tabSearch, company: companyId } });
        } else {
          navigate({ to: "/parent-companies", search: tabSearch });
        }
      }}
      title="Parent company"
      subtitle={name}
    >
      {companyId != null &&
        (expanded ? (
          <CompanyDetailContent companyId={companyId} />
        ) : (
          <CompanyQuickInfo companyId={companyId} />
        ))}
    </EntityDrawer>
  );
}
